
















































































import {
  defineComponent, reactive, toRefs, PropType, watch, computed,
} from '@vue/composition-api'

import { filterObjectByKeys } from '@/lib/utils/filterObjectByKeys'
import { FormFields, isFormField, FieldType } from '@/components/types/FormFields'

import TabularList from '../TabularList.vue'
import MyForm from './MyForm.vue'

/**
 * The shape of the prop input for the TabularList
 * component
 */
interface TabularListFields {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: {label: string; value: any; type?: FieldType; actionIcon?: string};
  }

/**
 * Shape of the Fields prop: a typescript intersection
 * between proper FormFields and objects only containing
 * a label/value, which is just used for non-editable
 * display.
 */
type Fields = FormFields & {
  [key: string]: {label: string; value?: unknown };
}

/**
 * The options to determine if whether or not
 * to edit all editable fields at once or only
 * a single field at a time, see prop of the
 * same name.
 */
type EditMode = 'singleField' | 'multipleFields'

type State = {
  /** The key of the fields currently being edited */
  editingFields: string[];
}

const initialState: State = {
  editingFields: [],
}

export default defineComponent({
  components: { TabularList, MyForm },
  props: {
    /**
     * Key/val object containing the fields to display with the shape
     * of Fields.
     */
    fields: {
      type: Object as PropType<Fields>,
      required: true,
    },
    /**
     * The parent can set this to true to display a spinner on the
     * form, e.g. while values are sent to an api for updating.
     */
    processing: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * The parent can set this to true when processing has
     * terminated successfully (it will stops spinners, etc).
     * Defaults to undefined.
     */
    success: {
      type: Boolean,
      required: false,
      default: undefined,
    },
    /**
     * 'singleField' (default) or 'multipleFields'
     * Determines whether each editable field should be provided
     * with an 'edit' btn/icon that will open a form containing
     * only that field (the default value: 'singleField'), or if
     * no such 'single field' editing button should be provided,
     * meaning you must trigger the edit action externally (see
     * the `editToggle` prop).
     */
    editMode: {
      type: String as PropType<EditMode>,
      required: false,
      default: () => 'singleField',
    },
    /**
     * A prop that the parent can use to toggle edit (the form)
     * on/off. Toggling on will put all editable fields in a form,
     * so it cannot be used for editing single fields separately.
     * The prop simply acts a change signal, so any change in the
     * value whether true->false or false->true will result in a
     * toggle (e.g. from shown/hidden depending on the previous
     * state).
     */
    editToggle: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Whether or not it should initially start in editing mode,
     * e.g. as a form (instead of a list).
     */
    edit: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * An optional message that can be displayed in
     * the non-edit state, if a field has no value
     */
    missingValueMessage: {
      type: String,
      required: false,
      default: 'not specified',
    },

  },

  setup(props, context) {
    const state = reactive({ ...initialState })

    function cancelEdit() {
      state.editingFields = []
      context.emit('form-closed')
    }

    /**
     * Maps object of type Fields to object of type TabularListFields which is
     * used as a prop for TabularList component.
     *
     * If showEditIconIfEditable is true editable Fields will be endowed with
     * an edit-icon
     */
    function mapFieldsToTabularList(
      myFields: Fields,
      showEditIconIfEditable: boolean,
    ): TabularListFields {
      const theList: TabularListFields = {}
      // eslint-disable-next-line no-restricted-syntax
      for (const [name, field] of Object.entries(myFields)) {
        theList[name] = {
          label: field.label,
          type: field.type,
          // som form fields may have a 'valueText' prop to show instead of a the actual value
          // eslint-disable-next-line max-len
          value: (field as {valueText?: string}).valueText ? (field as {valueText?: string}).valueText : field.value,
          actionIcon: field.editable && showEditIconIfEditable ? 'mdi-pencil' : undefined,
        }
      }
      return theList
    }

    /**
     * Maps object of type Fields to object of type FormFields which are used as
     * a prop for MyForm component. Note: you should filter out non-editable fields
     * before using this function.
     */
    function mapFieldsToFormFields(myFields: Fields): FormFields {
      const theFields: FormFields = {}
      // eslint-disable-next-line no-restricted-syntax
      for (const [name, field] of Object.entries(myFields)) {
        if (isFormField(field)) {
          theFields[name] = field
        }
      }
      return theFields
    }

    /**
     * Returns an object containing only those Fields in myFields that are
     * editable.
     */
    function getEditableFields(myFields: Fields): Fields {
      const editableFields: Fields = {}
      // eslint-disable-next-line no-restricted-syntax
      for (const [name, field] of Object.entries(myFields)) {
        if (field.editable) {
          editableFields[name] = field
        }
      }
      return editableFields
    }

    /**
     * This method evokes a form on the field/row specified by `fieldName`,
     * which must be a key in `props.fields`. If no fieldName is provided,
     * the form will contain all the editable fields in props.fields.
     */
    function edit(fieldName?: string) {
      if (fieldName) {
        state.editingFields = [fieldName]
        return
      }
      state.editingFields = Object.keys(getEditableFields(props.fields))
    }

    /**
     * Returns an object of type FormFields (used in the
     * template as a prop for MyForm component). The object
     * is mapped from props.fields
     */
    function getFormFields(): FormFields {
      return mapFieldsToFormFields(filterObjectByKeys(props.fields, state.editingFields))
    }

    /**
     * Returns an object of type TabularListFields (used in
     * the template as a prop for TabularListFields component).
     * The object is mapped from props.fields
     */
    function getTabularListFields(): TabularListFields {
      return mapFieldsToTabularList(props.fields, props.editMode === 'singleField')
    }

    function onEditFieldClicked(fieldName: string) {
      edit(fieldName)
    }

    /**
     * Emits a `save` event along with a key/value object containing
     * only those fields (keys) which values are changed.
     *
     * Note: the change object is handled by the child component MyForm,
     * we just call the method with what we is emitted from MyForm.
     */
    function onSaveNewValues(changed: {[key: string]: unknown}) {
      context.emit('save', changed)
    }

    const isEditing = computed(() => state.editingFields.length > 0)

    // Watch for when parent toggles edit on/off
    watch(() => props.editToggle, () => {
      if (isEditing.value) {
        cancelEdit()
      } else {
        edit()
      }
    })

    // Watch for the edit prop
    watch(() => props.edit, (newVal: boolean) => {
      if (newVal) {
        if (!isEditing.value) edit()
      } else if (isEditing.value) cancelEdit()
    })

    // Watch for when something stops processing
    watch(() => props.processing, (newProcessing: boolean, prevProcessing: boolean) => {
      // processing turned from true to false
      if (newProcessing === false && prevProcessing === true) {
        if (props.success) cancelEdit()
      }
    })

    return {
      ...toRefs(state),
      getFormFields,
      getTabularListFields,
      onEditFieldClicked,
      onSaveNewValues,
      cancelEdit,
      // isEditing,
    }
  },
})

